import { React, useState, useEffect, createContext, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import styles from "../assets/css/modules/shipmentReport.module.css";
import axiosInstance from "../helpers/AxiosInstance";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Header,
  Title,
  ShipmentsReportsTable,
  ButtonsContainer,
  LoadingButton,
} from "../components/utilities";
import NewPrintBtn from "../components/utilities/NewPrintBtn";

// ---------- STYLED BUTTONS (Tabs) ----------
const Button = styled.button`
  position: relative;
  cursor: pointer;
  min-width: 9.5%;
  height: 50px;
  background: transparent;
  font: normal normal 900 15px Cairo;
  border: none;
  border-bottom: 2px solid #eaeaea;
  color: ${(props) =>
    props["aria-selected"] ? "var(--primary-color)" : "#757373"};
  outline: none;
  transition: color 0.2s, border-color 0.2s;

  @media (max-width: 1440px) {
    font-size: 12px;
  }

  &:hover {
    color: var(--primary-color);
    border-bottom-color: var(--primary-color);
  }

  /* Rounded top corners like tabs */
  border-top-left-radius: ${(props) =>
    props.isFirst && !props.isRTL ? "12px" : "0"};
  border-top-right-radius: ${(props) =>
    props.isLast && !props.isRTL ? "12px" : "0"};

  /* RTL rounding */
  ${(props) =>
    props.isRTL &&
    `
    border-top-right-radius: ${props.isFirst ? "12px" : "0"};
    border-top-left-radius: ${props.isLast ? "12px" : "0"};
  `}

  /* Active underline */
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -2px;
    height: 3px;
    transform: scaleX(${(props) => (props["aria-selected"] ? 1 : 0)});
    transition: transform 0.25s ease;
    background: var(--primary-color);
  }

  /* Focus ring for accessibility */
  &:focus-visible {
    box-shadow: 0 0 0 3px #0000001f;
    border-radius: 12px 12px 0 0;
  }
`;

// ---------- CONTEXT ----------
export const selectedContext = createContext();

// ---------- COMPONENT ----------
export default function ShipmentsReport() {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation("reports");
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const instance = axiosInstance();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState([]);
  const [refetchKey, setRefetchKey] = useState(1);
  const [fetchingData, setFetchingData] = useState(true);
  const [mergedData, setMergedData] = useState([]);
  const [activeStatus, setActiveStatus] = useState("allShipments");
  const [statusID, setStatusID] = useState(0);

  const categories = [
    { statusID: 0, name: "allShipments" },
    { statusID: 5, name: "delivered" },
    { statusID: 4, name: "heading_to_customer" },
    { statusID: 3, name: "processing" },
    { statusID: 1, name: "new_orders" },
    { statusID: 6, name: "Require_Attention" },
    { statusID: 7, name: "pending_return" },
    { statusID: 8, name: "returned" },
    { statusID: -1, name: "payment_value" },
    { statusID: -2, name: "expected_payments" },
  ];

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const toDate = new Date();
  toDate.setDate(toDate.getDate() + 1);
  toDate.setHours(toDate.getHours() + 3);

  const fetchShipments = async (statusID) => {
    if (!fetchingData) return;
    await delay(300);
    try {
      const data = await instance.post("/GetShipmentsPage2", {
        mainStatusID: statusID,
        FromDate: "2020-04-20T13:23:30.141Z",
        ToDate: toDate.toISOString(),
        pageParam: {
          pageSize: 500,
          pageNumber: refetchKey,
        },
      });

      if (data.data.shipments.length === 0) {
        setFetchingData(false);
      }

      return data.data.shipments.map((shipment) => ({
        ...shipment,
        NetCOD: shipment.COD - shipment.ShipmentFees,
        runnerData: `${shipment.RunnerName} / ${shipment.RunnerMobile}`,
      }));
    } catch (error) {
      setFetchingData(false);
      if (error.response && error.response.status === 500) {
        navigate("/server-error");
      }
    }
  };

  const { refetch } = useQuery(
    ["reports", statusID, refetchKey],
    () => fetchShipments(statusID),
    {
      enabled: false,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      cacheTime: 1000,
      staleTime: 1000,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setRefetchKey((prev) => prev + 1);
          setMergedData((prevData) => [...prevData, ...data]);
        }
      },
    }
  );

  useEffect(() => {
    if (statusID !== null && fetchingData) {
      refetch();
    }
  }, [statusID, refetchKey, fetchingData]);

  useEffect(() => {
    const reportParam = searchParams.get("report");
    const category = categories.find((c) => c.name === reportParam);

    if (category && reportParam !== activeStatus) {
      setActiveStatus(reportParam);
      setStatusID(category.statusID);
      setFetchingData(true);
      setMergedData([]);
      setRefetchKey(1);
    }
  }, [searchParams]);

  const changeUrl = (value) => {
    setSearchParams(`report=${value}`);
  };

  const changeActiveButton = (eOrIndex) => {
    let status;
    if (typeof eOrIndex === "number") {
      status = categories[eOrIndex]?.name;
    } else {
      status = eOrIndex.target.dataset.status;
    }
    const category = categories.find((c) => c.name === status);
    if (!category) return;
    queryClient.removeQueries("reports");
    setActiveStatus(status);
    setStatusID(category.statusID);
    setFetchingData(true);
    setMergedData([]);
    setRefetchKey(1);
    changeUrl(status);
  };

  // ------- Keyboard navigation (Left/Right/Home/End) -------
  const tabsRef = useRef([]);
  const activeIndex = Math.max(
    0,
    categories.findIndex((c) => c.name === activeStatus)
  );

  const onTabsKeyDown = (e) => {
    const key = e.key;
    const dir = isRTL ? -1 : 1; // RTL يعكس الاتجاه
    let nextIndex = activeIndex;

    if (key === "ArrowRight")
      nextIndex = (activeIndex + dir + categories.length) % categories.length;
    else if (key === "ArrowLeft")
      nextIndex = (activeIndex - dir + categories.length) % categories.length;
    else if (key === "Home") nextIndex = 0;
    else if (key === "End") nextIndex = categories.length - 1;
    else return;

    e.preventDefault();
    tabsRef.current[nextIndex]?.focus();
    changeActiveButton(nextIndex);
  };

  return (
    <selectedContext.Provider value={{ selected, setSelected }}>
      <Box
        className={styles.headerContainer}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          alignContent: "flex-start",
        }}
      >
        <Header>
          <Title>{t("shipments_reports")}</Title>
        </Header>
        <NewPrintBtn
          print="shipments"
          data={selected}
          style={{ marginLeft: isRTL ? "0" : "30px" }}
        />
      </Box>

      <Box sx={{ marginTop: "10px", width: "100%" }}>
        <ButtonsContainer
          isRTL={i18n.language === "ar"}
          disabled={fetchingData}
          onKeyDown={onTabsKeyDown}
          role="tablist"
          aria-orientation="horizontal"
          style={{
            marginLeft: isRTL ? 0 : "25px",
            marginRight: isRTL ? "25px" : 0,
            paddingBottom: "6px",
          }}
        >
          {categories.map((cat, index) => {
            const selectedTab = activeStatus === cat.name;
            return (
              <Button
                isRTL={i18n.language === "ar"}
                isFirst={index === 0}
                isLast={index === categories.length - 1}
                key={cat.name}
                data-status={cat.name}
                onClick={changeActiveButton}
                role="tab"
                aria-selected={selectedTab}
                aria-controls={`panel-${cat.name}`}
                id={`tab-${cat.name}`}
                ref={(el) => (tabsRef.current[index] = el)}
                tabIndex={selectedTab ? 0 : -1}
                disabled={fetchingData && selectedTab}
              >
                {t(cat.name)}
                {selectedTab && fetchingData && <LoadingButton />}
              </Button>
            );
          })}
        </ButtonsContainer>
      </Box>

      <Box
        id={`panel-${activeStatus}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeStatus}`}
        sx={{ margin: "10px", width: "100%" }}
      >
        <ShipmentsReportsTable rows={mergedData} />
      </Box>
    </selectedContext.Provider>
  );
}
